import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Wrench,
  Search,
  Plus,
  Package,
  MapPin,
  Filter,
  Grid,
  List,
  BarChart2,
  Camera,
  Download,
  Edit,
  Trash2,
  Eye,
  Clock,
  AlertCircle,
  CheckCircle,
  Settings,
  Users,
  FolderOpen,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  CalendarCheck,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

// Sous-composants
import OutilForm from './components/OutilForm';
import CameraCapture from './components/CameraCapture';
import VueAtelier from './components/VueAtelier';
import GestionEmprunts from './components/GestionEmprunts';
import ExportPDF from './components/ExportPDF';
import GestionCategories from './components/GestionCategories';
import GestionEmplacements from './components/GestionEmplacements';
import GestionControles from './components/GestionControles';
import HistoriquePDF from './components/HistoriquePDF';

const GestionMateriel = () => {
  // États principaux
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard'); // dashboard, liste, atelier, emprunts, categories, emplacements, controles, historique-pdf
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [outils, setOutils] = useState([]);
  const [ateliers, setAteliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    mecanique: 0,
    carrosserie: 0,
    empruntes: 0,
    retard: 0,
    valeur: 0,
    alertes: 0
  });

  // Filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterCategorie, setFilterCategorie] = useState('all');
  const [filterAtelier, setFilterAtelier] = useState('all');
  const [filterDisponible, setFilterDisponible] = useState('all');

  // Modals
  const [showAddOutil, setShowAddOutil] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedOutil, setSelectedOutil] = useState(null);
  const [showExport, setShowExport] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const handleEditOutil = (outil) => {
    setSelectedOutil(outil);
    setShowAddOutil(true);
  };

  const handleAddOutilInArmoire = (emplacement) => {
    setSelectedOutil({
      emplacement_id: emplacement.id,
      emplacement_nom: emplacement.nom
    });
    setShowAddOutil(true);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      console.log('📦 [GestionMateriel] Chargement des données...');

      const { data: ateliersData, error: ateliersError } = await supabase
        .from('mat_ateliers')
        .select('*')
        .order('code');

      if (ateliersError) throw ateliersError;

      const { data: categoriesData, error: categoriesError } = await supabase
        .from('mat_categories')
        .select('*')
        .order('nom');

      if (categoriesError) throw categoriesError;

      const { data: emplacementsData, error: emplacementsError } = await supabase
        .from('mat_emplacements')
        .select(`
          *,
          atelier:mat_ateliers(*)
        `)
        .order('ordre_affichage');

      if (emplacementsError) throw emplacementsError;

      const { data: outilsData, error: outilsError } = await supabase
        .from('v_mat_outils_complets')
        .select('*');

      if (outilsError) throw outilsError;

      setAteliers(ateliersData || []);
      setCategories(categoriesData || []);
      setEmplacements(emplacementsData || []);
      setOutils(outilsData || []);

      // Calculer les alertes
      const alertes = (outilsData || []).filter(o => 
        o.statut_alerte && o.statut_alerte !== 'ok'
      ).length;

      const statsData = {
        total: outilsData?.length || 0,
        mecanique: outilsData?.filter(o => o.type_formation === 'mecanique').length || 0,
        carrosserie: outilsData?.filter(o => o.type_formation === 'carrosserie').length || 0,
        empruntes: outilsData?.filter(o => !o.disponible).length || 0,
        retard: 0,
        valeur: 0,
        alertes
      };

      setStats(statsData);
      console.log('✅ [GestionMateriel] Données chargées:', statsData);
    } catch (error) {
      console.error('❌ [GestionMateriel] Erreur:', error);
      alert('Erreur lors du chargement des données: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const outilsFiltres = outils.filter(outil => {
    if (searchTerm && !outil.nom.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !outil.reference?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterType !== 'all' && outil.type_formation !== filterType) {
      return false;
    }
    if (filterCategorie !== 'all' && outil.categorie_id !== filterCategorie) {
      return false;
    }
    if (filterAtelier !== 'all' && outil.atelier_code !== filterAtelier) {
      return false;
    }
    if (filterDisponible === 'disponible' && !outil.disponible) {
      return false;
    }
    if (filterDisponible === 'emprunte' && outil.disponible) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-white text-xl">Chargement du matériel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex">
      {/* SIDEBAR VERTICALE */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarCollapsed ? '80px' : '280px' }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="bg-slate-800/95 backdrop-blur-sm border-r border-slate-600/30 flex flex-col shadow-2xl"
      >
        <div className="pt-32"></div>

        {/* Boutons Actions Principaux */}
        <div className="px-4 mb-6 space-y-3">
          {!sidebarCollapsed && (
            <>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddOutil(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-lg font-medium"
              >
                <Plus className="w-5 h-5" />
                Ajouter un outil
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowExport(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition-colors shadow-lg font-medium"
              >
                <Download className="w-5 h-5" />
                Export PDF
              </motion.button>
            </>
          )}
          
          {sidebarCollapsed && (
            <>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowAddOutil(true)}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-lg flex items-center justify-center transition-colors shadow-lg"
                title="Ajouter un outil"
              >
                <Plus className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setShowExport(true)}
                className="w-full bg-green-600 hover:bg-green-700 text-white p-3 rounded-lg flex items-center justify-center transition-colors shadow-lg"
                title="Export PDF"
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </>
          )}
        </div>

        {/* Bouton Toggle Sidebar */}
        <div className="px-4 mb-6">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full p-2 rounded-lg bg-slate-700/50 hover:bg-slate-600 text-gray-400 hover:text-white transition-colors flex items-center justify-center"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </motion.button>
        </div>

        <div className="px-4 mb-6">
          <div className="border-t border-slate-600/50"></div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-4 space-y-3 overflow-y-auto">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('dashboard')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'dashboard'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Tableau de bord' : ''}
          >
            <BarChart2 className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Tableau de bord</span>}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('liste')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'liste'
                ? 'bg-green-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Liste complète' : ''}
          >
            <List className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Liste complète</span>}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('atelier')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'atelier'
                ? 'bg-orange-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Par atelier' : ''}
          >
            <MapPin className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Par atelier</span>}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('emprunts')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors relative ${
              view === 'emprunts'
                ? 'bg-purple-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Emprunts' : ''}
          >
            <Clock className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">Emprunts</span>
                {stats.empruntes > 0 && (
                  <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                    {stats.empruntes}
                  </span>
                )}
              </>
            )}
            {sidebarCollapsed && stats.empruntes > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            )}
          </motion.button>

          {/* NOUVELLE PAGE: Contrôles & Péremptions */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('controles')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors relative ${
              view === 'controles'
                ? 'bg-amber-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Contrôles & Péremptions' : ''}
          >
            <CalendarCheck className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && (
              <>
                <span className="flex-1 text-left">Contrôles</span>
                {stats.alertes > 0 && (
                  <motion.span
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ repeat: Infinity, duration: 2 }}
                    className="bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    {stats.alertes}
                  </motion.span>
                )}
              </>
            )}
            {sidebarCollapsed && stats.alertes > 0 && (
              <motion.span
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full"
              ></motion.span>
            )}
          </motion.button>

          {/* NOUVELLE PAGE: Historique PDF */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('historique-pdf')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'historique-pdf'
                ? 'bg-indigo-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Historique PDF' : ''}
          >
            <FileText className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Historique PDF</span>}
          </motion.button>

          <div className="py-2">
            <div className="border-t border-slate-600/30"></div>
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('categories')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'categories'
                ? 'bg-pink-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Catégories' : ''}
          >
            <FolderOpen className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Catégories</span>}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setView('emplacements')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              view === 'emplacements'
                ? 'bg-teal-600 text-white shadow-lg'
                : 'bg-slate-700/50 text-gray-300 hover:bg-slate-600'
            }`}
            title={sidebarCollapsed ? 'Emplacements' : ''}
          >
            <Settings className="w-5 h-5 flex-shrink-0" />
            {!sidebarCollapsed && <span className="flex-1 text-left">Emplacements</span>}
          </motion.button>
        </nav>

        {/* Sidebar Footer */}
        <div className="p-4 border-t border-slate-700 mt-4">
          {!sidebarCollapsed && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-xs text-gray-400 text-center"
            >
              <p className="font-semibold text-white mb-1 text-lg">{stats.total}</p>
              <p className="mb-1">outils au total</p>
              {stats.alertes > 0 && (
                <motion.p
                  animate={{ color: ["#f59e0b", "#ef4444", "#f59e0b"] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="flex items-center justify-center gap-1"
                >
                  <AlertTriangle className="w-3 h-3" />
                  {stats.alertes} alerte(s)
                </motion.p>
              )}
            </motion.div>
          )}
          {sidebarCollapsed && (
            <div className="text-center">
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
          )}
        </div>
      </motion.aside>

      {/* CONTENU PRINCIPAL */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-32">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white mb-2">
                Gestion du Matériel
              </h1>
              <p className="text-gray-400">
                CFA Est Loiret - Montargis
              </p>
            </div>
          </motion.div>

          {/* Statistiques (Dashboard uniquement) */}
          {view === 'dashboard' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8"
            >
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <Package className="w-8 h-8 text-blue-500" />
                  <span className="text-3xl font-bold text-white">{stats.total}</span>
                </div>
                <p className="text-gray-400">Total outils</p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <Wrench className="w-8 h-8 text-green-500" />
                  <span className="text-3xl font-bold text-white">{stats.mecanique}</span>
                </div>
                <p className="text-gray-400">Mécanique</p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <Settings className="w-8 h-8 text-orange-500" />
                  <span className="text-3xl font-bold text-white">{stats.carrosserie}</span>
                </div>
                <p className="text-gray-400">Carrosserie</p>
              </div>

              <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                <div className="flex items-center justify-between mb-2">
                  <Clock className="w-8 h-8 text-purple-500" />
                  <span className="text-3xl font-bold text-white">{stats.empruntes}</span>
                </div>
                <p className="text-gray-400">Empruntés</p>
              </div>

              <div 
                className={`bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border cursor-pointer transition-all hover:scale-105 ${
                  stats.alertes > 0 ? 'border-red-500/50' : 'border-slate-700'
                }`}
                onClick={() => setView('controles')}
              >
                <div className="flex items-center justify-between mb-2">
                  <AlertTriangle className={`w-8 h-8 ${stats.alertes > 0 ? 'text-red-500' : 'text-green-500'}`} />
                  <span className={`text-3xl font-bold ${stats.alertes > 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {stats.alertes}
                  </span>
                </div>
                <p className="text-gray-400">Alertes contrôle</p>
              </div>
            </motion.div>
          )}

          {/* Contenu selon la vue */}
          <AnimatePresence mode="wait">
            {view === 'dashboard' && (
              <motion.div
                key="dashboard"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <FolderOpen className="w-6 h-6 text-blue-500" />
                      Par catégorie
                    </h3>
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {categories.map(cat => {
                        const count = outils.filter(o => o.categorie_id === cat.id).length;
                        if (count === 0) return null;
                        return (
                          <div key={cat.id} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                            <span className="text-white">{cat.nom}</span>
                            <span className="text-gray-400">{count} outil(s)</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700">
                    <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                      <MapPin className="w-6 h-6 text-green-500" />
                      Par atelier
                    </h3>
                    <div className="space-y-4">
                      {ateliers.map(atelier => {
                        const count = outils.filter(o => o.atelier_code === atelier.code).length;
                        return (
                          <div key={atelier.id} className="p-4 bg-slate-700/50 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white font-semibold">{atelier.nom}</span>
                              <span className="text-gray-400">{count} outil(s)</span>
                            </div>
                            <div className="w-full bg-slate-600 rounded-full h-2">
                              <div
                                className="bg-blue-500 h-2 rounded-full"
                                style={{ width: `${stats.total > 0 ? (count / stats.total) * 100 : 0}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {view === 'liste' && (
              <motion.div
                key="liste"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="bg-slate-800/50 backdrop-blur-sm rounded-xl p-6 border border-slate-700"
              >
                <h3 className="text-2xl font-semibold text-white mb-6">Liste complète</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
                  />
                  <select
                    value={filterCategorie}
                    onChange={(e) => setFilterCategorie(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Toutes les catégories</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nom}</option>
                    ))}
                  </select>
                  <select
                    value={filterAtelier}
                    onChange={(e) => setFilterAtelier(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tous les ateliers</option>
                    {ateliers.map(atelier => (
                      <option key={atelier.id} value={atelier.code}>{atelier.nom}</option>
                    ))}
                  </select>
                  <select
                    value={filterDisponible}
                    onChange={(e) => setFilterDisponible(e.target.value)}
                    className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">Tous les états</option>
                    <option value="disponible">Disponibles</option>
                    <option value="emprunte">Empruntés</option>
                  </select>
                </div>

                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {outilsFiltres.length === 0 ? (
                    <div className="text-center py-12 text-gray-400">
                      <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Aucun outil trouvé</p>
                    </div>
                  ) : (
                    outilsFiltres.map(outil => (
                      <div key={outil.id} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg hover:bg-slate-700 transition-colors">
                        <div className="flex items-center gap-4">
                          {outil.photo_url ? (
                            <img src={outil.photo_url} alt={outil.nom} className="w-16 h-16 rounded-lg object-cover" />
                          ) : (
                            <div className="w-16 h-16 rounded-lg bg-slate-600 flex items-center justify-center">
                              <Package className="w-8 h-8 text-gray-400" />
                            </div>
                          )}
                          <div>
                            <h4 className="text-white font-semibold flex items-center gap-2">
                              {outil.nom}
                              {outil.statut_alerte && outil.statut_alerte !== 'ok' && (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              )}
                            </h4>
                            <p className="text-sm text-gray-400">{outil.reference}</p>
                            <p className="text-xs text-gray-500">{outil.categorie_nom} • {outil.emplacement_nom}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            outil.disponible
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}>
                            {outil.disponible ? 'Disponible' : 'Emprunté'}
                          </span>
                          <button
                            onClick={() => {
                              setSelectedOutil(outil);
                              setShowAddOutil(true);
                            }}
                            className="p-2 text-blue-400 hover:bg-slate-600 rounded-lg transition-colors"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </motion.div>
            )}

            {view === 'atelier' && (
              <VueAtelier ateliers={ateliers} emplacements={emplacements} outils={outils} onRefresh={loadData} />
            )}

            {view === 'emprunts' && (
              <GestionEmprunts outils={outils} onRefresh={loadData} />
            )}

            {view === 'categories' && (
              <GestionCategories />
            )}

            {view === 'emplacements' && (
              <GestionEmplacements 
                onEditOutil={handleEditOutil}
                onRefresh={loadData}
                onAddOutilInArmoire={handleAddOutilInArmoire}
              />
            )}

            {/* NOUVELLE VUE: Contrôles */}
            {view === 'controles' && (
              <GestionControles 
                onEditOutil={handleEditOutil}
                onRefresh={loadData}
              />
            )}

            {/* NOUVELLE VUE: Historique PDF */}
            {view === 'historique-pdf' && (
              <HistoriquePDF onRefresh={loadData} />
            )}
          </AnimatePresence>

          {/* Modals */}
          {showAddOutil && (
            <OutilForm
              onClose={() => {
                setShowAddOutil(false);
                setSelectedOutil(null);
              }}
              onSave={loadData}
              ateliers={ateliers}
              categories={categories}
              emplacements={emplacements}
              outil={selectedOutil}
            />
          )}

          {showExport && (
            <ExportPDF
              outils={outils}
              ateliers={ateliers}
              onClose={() => setShowExport(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default GestionMateriel;